/**
 * Which Colyseus room name a client may ask `joinOrCreate` for, and how `/arena.html?room=`
 * resolves to one.
 *
 * Five rooms exist on the realtime tier (`realtime/src/rooms/arena.room.ts`, `ROOM_MODE_KEY`,
 * and their registration in `realtime/src/server.ts`): `arena` (online TDM, or whatever
 * `ARENA_MODE` names), `arena-dm` (online deathmatch), `arena-competitive` (the ranked
 * 12-round format), `arena-dm-bots` (solo deathmatch vs bots) and `arena-tdm-bots` (solo 5v5
 * vs bots). The room's mode is pinned server-side by its *name* — never by anything a client
 * sends in `joinOrCreate` options — so this whitelist grants a client no choice the server
 * does not already offer through these five names. What it exists to stop is an unvalidated query-string value
 * reaching `client.joinOrCreate()` outright: a typo, a stale bookmark, or a deliberately hostile
 * value should fall back to the normal room rather than being handed to the transport as-is.
 */
export const ARENA_ROOMS = [
  'arena',
  'arena-dm',
  'arena-competitive',
  'arena-dm-bots',
  'arena-tdm-bots',
  'arena-competitive-bots',
] as const;

export type ArenaRoomName = (typeof ARENA_ROOMS)[number];

export const DEFAULT_ARENA_ROOM: ArenaRoomName = 'arena';

function isArenaRoomName(value: string): value is ArenaRoomName {
  return (ARENA_ROOMS as readonly string[]).includes(value);
}

/**
 * Resolves `?room=` out of a `location.search`-shaped string to a known room name.
 *
 * Absent, empty or unrecognised all fall back to the default room rather than throwing or
 * passing the raw value through — the same "unknown falls back to the shipped default" shape
 * `?map=` already uses a few lines below this one in `main.ts`, and deliberately not the strict
 * "unknown throws" shape `gameModeByKey()` uses for a *server-side* mode key: that function's
 * callers are never client-supplied (see its own doc comment), and this one always is.
 */
export function resolveArenaRoom(search: string): ArenaRoomName {
  const requested = new URLSearchParams(search).get('room');
  return requested !== null && isArenaRoomName(requested) ? requested : DEFAULT_ARENA_ROOM;
}
